import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const fieldBase =
  'w-full rounded-xl border bg-surface px-3.5 text-base text-ink placeholder:text-ink-muted/80 transition-[border-color,box-shadow] duration-150 focus:outline-none focus-visible:outline-none focus:border-action focus:ring-4 focus:ring-royal-500/20 disabled:opacity-60';

interface FieldProps {
  label?: string;
  hint?: ReactNode;
  error?: string | null;
  /** Rendered inside the input's right edge, e.g. a show password toggle. */
  trailing?: ReactNode;
  wrapperClassName?: string;
}

export type InputProps = InputHTMLAttributes<HTMLInputElement> & FieldProps;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, trailing, className, wrapperClassName, id, ...props },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const hintId = `${inputId}-hint`;
  const errId = `${inputId}-err`;
  return (
    <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-ink">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={cn(error && errId, hint && hintId) || undefined}
          className={cn(fieldBase, 'h-11', error ? 'border-danger-500' : 'border-line', trailing && 'pr-11', className)}
          {...props}
        />
        {trailing && <div className="absolute inset-y-0 right-1 flex items-center">{trailing}</div>}
      </div>
      {hint && !error && (
        <div id={hintId} className="text-xs text-ink-muted">
          {hint}
        </div>
      )}
      {error && (
        <p id={errId} role="alert" className="text-sm text-danger-600 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & Omit<FieldProps, 'trailing'>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, wrapperClassName, id, ...props },
  ref,
) {
  const auto = useId();
  const taId = id ?? auto;
  return (
    <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
      {label && (
        <label htmlFor={taId} className="text-sm font-medium text-ink">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={taId}
        aria-invalid={error ? true : undefined}
        className={cn(fieldBase, 'min-h-24 py-2.5', error ? 'border-danger-500' : 'border-line', className)}
        {...props}
      />
      {hint && !error && <div className="text-xs text-ink-muted">{hint}</div>}
      {error && (
        <p role="alert" className="text-sm text-danger-600 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  );
});
